import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import Pica from 'pica';
import { useDropzone } from 'react-dropzone';
import Draggable from 'react-draggable'; // Import Draggable
import '../styles/App.css';
import ImageDisplay from '../components/app/ImageDisplay';
import CropFrame from '../components/app/CropFrame';
import SmartCropDialog from '../components/app/SmartCropDialog';
import { Rnd } from 'react-rnd';
import FocusArea from '../components/app/FocusArea';
import Tooltip from '../components/ui/Tooltip';

function Home({ onFileDrop }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [cropFrames, setCropFrames] = useState([]);
  const [croppedImages, setCroppedImages] = useState([]);
  const [selectedCropIndex, setSelectedCropIndex] = useState(-1);
  const [zoomFactor, setZoomFactor] = useState(1);
  const [addCropButtonVisible, setAddCropButtonVisible] = useState(true);
  const [isDraggingOverWindow, setIsDraggingOverWindow] = useState(false);
  const [isZoomInHovered, setIsZoomInHovered] = useState(false);
  const [isZoomOutHovered, setIsZoomOutHovered] = useState(false);
  const [showDownloads, setShowDownloads] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [cropLayerNames, setCropLayerNames] = useState([]);
  const [isDraggedImage, setIsDraggedImage] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [focusRect, setFocusRect] = useState(null); // {x, y, width, height} or null
  const zoomContainerRef = useRef(null);
  const imageRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);  // Track if the user is in draw mode
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  const [isDraggingFrame, setIsDraggingFrame] = useState(false);  // Track if a frame is being drawn
  const [recentlyDrawnIndex, setRecentlyDrawnIndex] = useState(-1);
  const [editingLayerIndex, setEditingLayerIndex] = useState(-1);
  const [editLayerWidth, setEditLayerWidth] = useState(0);
  const [editLayerHeight, setEditLayerHeight] = useState(0);
  const [smartDialogVisible, setSmartDialogVisible] = useState(false);
  const [smartFrameEnabled, setSmartFrameEnabled] = useState(false);
  const [dialogDrawMode, setDialogDrawMode] = useState(false);
  const [dialogFrameStartIndex, setDialogFrameStartIndex] = useState(0);
  const [frameAnchor, setFrameAnchor] = useState({ x: 0.5, y: 0.5 });
  const [smartLoading, setSmartLoading] = useState(false);
  const [cropLoading, setCropLoading] = useState(false);
  const [pendingOversizeFrames, setPendingOversizeFrames] = useState(null);
  const [toolbarPosition, setToolbarPosition] = useState(null);
  const [toolbarPositioned, setToolbarPositioned] = useState(false);
  const [cropDropdownOpen, setCropDropdownOpen] = useState(false);
  const [clearDropdownOpen, setClearDropdownOpen] = useState(false);
  const toolbarRef = useRef(null);
  const layerWidthInputRef = useRef(null);

  const openFrameDialog = () => {
    if (smartDialogVisible) {
      closeSmartDialog();
      return;
    }
    if (smartFrameEnabled && !focusRect && imageDimensions.width && imageDimensions.height) {
      setFocusRect({
        x: imageDimensions.width / 4,
        y: imageDimensions.height / 4,
        width: imageDimensions.width / 2,
        height: imageDimensions.height / 2,
      });
    }
    setDialogFrameStartIndex(cropFrames.length);
    setSmartDialogVisible(true);
    setAddCropButtonVisible(false);
  };
  const closeSmartDialog = () => {
    setSmartDialogVisible(false);
    setSmartFrameEnabled(false);
    setDialogDrawMode(false);
    setIsDrawing(false);
    setAddCropButtonVisible(true);
  };

  const cancelFrameDialog = () => {
    setCropFrames((current) => current.slice(0, dialogFrameStartIndex));
    setCropLayerNames((current) => current.slice(0, dialogFrameStartIndex));
    setSelectedCropIndex(-1);
    closeSmartDialog();
  };

  const handleSmartFrameToggle = (enabled) => {
    if (enabled && !focusRect && imageDimensions.width && imageDimensions.height) {
      setFocusRect({
        x: imageDimensions.width / 4,
        y: imageDimensions.height / 4,
        width: imageDimensions.width / 2,
        height: imageDimensions.height / 2,
      });
    }
    setSmartFrameEnabled(enabled);
  };

  const handleFrameAnchorChange = (anchor) => {
    setFrameAnchor(anchor);
    setSmartFrameEnabled(false);
  };

  const handleDialogDrawModeChange = (enabled) => {
    setDialogDrawMode(enabled);
    setIsDrawing(enabled);
  };

  useEffect(() => {
    if (!smartDialogVisible || !dialogDrawMode) return undefined;
    const handleDrawModeKeyDown = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        setSmartDialogVisible(false);
        setSmartFrameEnabled(false);
        setDialogDrawMode(false);
        setIsDrawing(false);
        setAddCropButtonVisible(true);
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setCropFrames((current) => current.slice(0, dialogFrameStartIndex));
        setCropLayerNames((current) => current.slice(0, dialogFrameStartIndex));
        setSelectedCropIndex(-1);
        setSmartDialogVisible(false);
        setSmartFrameEnabled(false);
        setDialogDrawMode(false);
        setIsDrawing(false);
        setAddCropButtonVisible(true);
      }
    };
    window.addEventListener('keydown', handleDrawModeKeyDown);
    return () => window.removeEventListener('keydown', handleDrawModeKeyDown);
  }, [dialogDrawMode, dialogFrameStartIndex, smartDialogVisible]);

  useLayoutEffect(() => {
    if (!toolbarRef.current || toolbarPosition) return;
    setToolbarPosition({
      x: Math.max(0, (window.innerWidth - toolbarRef.current.offsetWidth) / 2),
      y: 0,
    });
  }, [toolbarPosition]);

  useEffect(() => {
    if (!toolbarPosition) return undefined;
    const frame = requestAnimationFrame(() => setToolbarPositioned(true));
    return () => cancelAnimationFrame(frame);
  }, [toolbarPosition]);

  useEffect(() => {
    if (editingLayerIndex !== -1) layerWidthInputRef.current?.focus();
  }, [editingLayerIndex]);

  useEffect(() => {
    if (!isDrawing) {
      setIsShiftHeld(false);
      return undefined;
    }
    const handleKeyDown = (event) => setIsShiftHeld(event.shiftKey);
    const handleKeyUp = (event) => setIsShiftHeld(event.shiftKey);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDrawing]);

  useEffect(() => {
    if (recentlyDrawnIndex === -1) return undefined;
    const timeout = setTimeout(() => setRecentlyDrawnIndex(-1), 500);
    return () => clearTimeout(timeout);
  }, [recentlyDrawnIndex]);

  const focusRectToBBox = (rect) => {
    // Convert focusRect {x, y, width, height} to [x, y, w, h] bbox
    if (!rect) return null;
    return [rect.x, rect.y, rect.width, rect.height];
  };

  const rectsIntersect = (a, b) => {
    // a, b: [x, y, w, h]
    return (
      a[0] < b[0] + b[2] &&
      a[0] + a[2] > b[0] &&
      a[1] < b[1] + b[3] &&
      a[1] + a[3] > b[1]
    );
  };

  const bboxCenterDist = (bbox, focus) => {
    // Distance from bbox center to focus center
    const cx1 = bbox[0] + bbox[2]/2;
    const cy1 = bbox[1] + bbox[3]/2;
    const cx2 = focus[0] + focus[2]/2;
    const cy2 = focus[1] + focus[3]/2;
    return Math.hypot(cx1 - cx2, cy1 - cy2);
  };

  const handleSmartCrop = async (dims) => {
    setSmartLoading(true);
    try {
      const tf = await import('@tensorflow/tfjs');
      const cocoSsd = await import('@tensorflow-models/coco-ssd');
      const model = await cocoSsd.load();
      const img = new window.Image();
      img.src = selectedImage;
      await new Promise((resolve) => { img.onload = resolve; });
      const predictions = await model.detect(img);
      console.log('COCO-SSD predictions:', predictions);
      let useFallback = !predictions.length;
      const classCounts = {};
      predictions.forEach(p => { classCounts[p.class] = (classCounts[p.class] || 0) + 1; });
      const mostCommonClass = Object.entries(classCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'center';
      let filtered = predictions.filter(p => p.class === mostCommonClass && p.score >= 0.5);
      if (!filtered.length) filtered = predictions;
      let faceBoxes = [];
      // If person detected, try MediaPipe Face Detection
      if (!useFallback && mostCommonClass === 'person') {
        try {
          const face = await import('@mediapipe/tasks-vision');
          const { FaceDetector, FilesetResolver } = face;
          const vision = await FilesetResolver.forVisionTasks(
            'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
          );
          const faceDetector = await FaceDetector.createFromOptions(vision, {
            baseOptions: { modelAssetPath: 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/face_detector/face_detector.tflite' },
            runningMode: 'IMAGE',
            minDetectionConfidence: 0.5
          });
          const imageEl = document.createElement('img');
          imageEl.src = selectedImage;
          await new Promise((resolve) => { imageEl.onload = resolve; });
          const detections = await faceDetector.detect(imageEl);
          if (detections.detections && detections.detections.length > 0) {
            faceBoxes = detections.detections.map(det => {
              const box = det.boundingBox;
              return [box.originX, box.originY, box.width, box.height];
            });
            // Replace filtered with face boxes as pseudo-predictions
            filtered = faceBoxes.map(bbox => ({ bbox, class: 'face', score: 1.0 }));
            useFallback = false;
            console.log('MediaPipe faces:', faceBoxes);
          }
        } catch (err) {
          console.warn('MediaPipe face detection failed:', err);
        }
      }
      // If no objects detected, center crops in the focus area (or image center)
      if (useFallback) {
        filtered = [{ bbox: [imageDimensions.width / 4, imageDimensions.height / 4, imageDimensions.width / 2, imageDimensions.height / 2], class: 'center', score: 1 }];
      }
      // If focusRect is present, prioritize objects inside or near it
      const focusBBox = focusRectToBBox(focusRect);
      if (!useFallback && focusBBox) {
        filtered = filtered.slice().sort((a, b) => {
          const aInter = rectsIntersect(a.bbox, focusBBox) ? 1 : 0;
          const bInter = rectsIntersect(b.bbox, focusBBox) ? 1 : 0;
          if (aInter !== bInter) return bInter - aInter;
          return bboxCenterDist(a.bbox, focusBBox) - bboxCenterDist(b.bbox, focusBBox);
        });
      } else if (!useFallback) {
        // Sort by area (largest first), then by centrality
        const centerX = imageDimensions.width / 2;
        const centerY = imageDimensions.height / 2;
        filtered.sort((a, b) => {
          const areaA = a.bbox[2] * a.bbox[3];
          const areaB = b.bbox[2] * b.bbox[3];
          if (areaA !== areaB) return areaB - areaA;
          const distA = Math.hypot((a.bbox[0] + a.bbox[2]/2) - centerX, (a.bbox[1] + a.bbox[3]/2) - centerY);
          const distB = Math.hypot((b.bbox[0] + b.bbox[2]/2) - centerX, (b.bbox[1] + b.bbox[3]/2) - centerY);
          return distA - distB;
        });
      }
      // Margin (10% of detected object size)
      const marginRatio = 0.1;
      const newFrames = [];
      dims.forEach((dim, i) => {
        let cropX, cropY, cropW = dim.width, cropH = dim.height;
        if (focusBBox) {
          const focusScale = Math.max(focusBBox[2] / cropW, focusBBox[3] / cropH, 1);
          cropW = Math.round(cropW * focusScale);
          cropH = Math.round(cropH * focusScale);
        }
        if (!useFallback) {
          const pred = filtered[i] || filtered[0];
          const [x, y, w, h] = pred.bbox;
          const padW = w * marginRatio;
          const padH = h * marginRatio;
          const objCenterX = x + w / 2;
          const objCenterY = y + h / 2;
          if (focusBBox && rectsIntersect(pred.bbox, focusBBox)) {
            const interLeft = Math.max(x, focusBBox[0]);
            const interTop = Math.max(y, focusBBox[1]);
            const interRight = Math.min(x + w, focusBBox[0] + focusBBox[2]);
            const interBottom = Math.min(y + h, focusBBox[1] + focusBBox[3]);
            const interW = interRight - interLeft;
            const interH = interBottom - interTop;
            const interCenterX = interLeft + interW / 2;
            const interCenterY = interTop + interH / 2;
            cropX = Math.round(interCenterX - cropW / 2);
            cropY = Math.round(interCenterY - cropH / 2);
          } else if (focusBBox) {
            cropX = Math.round(focusBBox[0] + focusBBox[2]/2 - cropW/2);
            cropY = Math.round(focusBBox[1] + focusBBox[3]/2 - cropH/2);
          } else {
            cropX = Math.round(objCenterX - cropW / 2 - padW);
            cropY = Math.round(objCenterY - cropH / 2 - padH);
          }
        } else {
          // Fallback: center in focus area if present, else image center
          let centerX, centerY;
          if (focusBBox) {
            centerX = focusBBox[0] + focusBBox[2]/2;
            centerY = focusBBox[1] + focusBBox[3]/2;
          } else {
            centerX = imageDimensions.width / 2;
            centerY = imageDimensions.height / 2;
          }
          cropX = Math.round(centerX - cropW / 2);
          cropY = Math.round(centerY - cropH / 2);
        }
        // Clamp to image bounds
        cropX = Math.max(0, Math.min(cropX, imageDimensions.width - cropW));
        cropY = Math.max(0, Math.min(cropY, imageDimensions.height - cropH));
        newFrames.push({ x: cropX, y: cropY, width: cropW, height: cropH, originalWidth: dim.width, originalHeight: dim.height });
      });
      setCropFrames((prev) => [...prev, ...newFrames]);
      setCropLayerNames((prev) => [
        ...prev,
        ...newFrames.map((f, i) => `Smart Crop (${useFallback ? 'center' : (faceBoxes.length ? 'face' : mostCommonClass)}) ${prev.length + i + 1}`)
      ]);
      // Google Analytics event for crop frame creation (smart crop)
      if (window.gtag) {
        window.gtag('event', 'create_crop_frame', {
          event_category: 'CropFrame',
          event_label: 'smart_crop',
          value: newFrames.length
        });
      }
    } catch (err) {
      alert('Smart crop failed: ' + err.message);
    }
    setSmartLoading(false);
    closeSmartDialog();
  };

  const handleMouseDown = useCallback((e) => {
    if (!isDrawing || !zoomContainerRef.current) return;
    if (e.shiftKey || e.target.closest('.toolbar, .smart-crop-dialog')) return;

    // Find the .zoomed-container inside the zoomContainer
    const zoomedContainer = zoomContainerRef.current.querySelector('.zoomed-container');
    if (!zoomedContainer) return;
    const rect = zoomedContainer.getBoundingClientRect();

    // Calculate the initial mouse position relative to the zoomed container, then divide by zoomFactor
    const startX = (e.clientX - rect.left) / zoomFactor;
    const startY = (e.clientY - rect.top) / zoomFactor;

    // Debugging log to check the start position
    console.log('startX:', startX, 'startY:', startY);

    // Ensure valid start positions
    if (isNaN(startX) || isNaN(startY)) {
      console.error('Invalid start positions', { startX, startY });
      return;
    }

    setCropFrames([...cropFrames, { x: startX, y: startY, width: 0, height: 0 }]);
    setSelectedCropIndex(cropFrames.length);
    setRecentlyDrawnIndex(-1);
    setIsDraggingFrame(true);
    // Google Analytics event for crop frame creation (draw mode)
    if (window.gtag) {
      window.gtag('event', 'create_crop_frame', {
        event_category: 'CropFrame',
        event_label: 'draw',
        value: 1
      });
    }
  }, [isDrawing, zoomFactor, cropFrames]);

  const forceUpdate = useState()[1].bind(null, {});

  const handleMouseMove = useCallback((e) => {
    if (!isDraggingFrame || selectedCropIndex === -1) return;

    // Find the .zoomed-container inside the zoomContainer
    const zoomedContainer = zoomContainerRef.current.querySelector('.zoomed-container');
    if (!zoomedContainer) return;
    const rect = zoomedContainer.getBoundingClientRect();
    const currentX = (e.clientX - rect.left) / zoomFactor;
    const currentY = (e.clientY - rect.top) / zoomFactor;

    setCropFrames((prevFrames) => {
      const startX = prevFrames[selectedCropIndex].x;
      const startY = prevFrames[selectedCropIndex].y;

      // Calculate new width and height, ensuring they are positive and valid
      const newWidth = Math.abs(currentX - startX) || 0;  // Prevent NaN, default to 0
      const newHeight = Math.abs(currentY - startY) || 0;  // Prevent NaN, default to 0

      // Set the new position if the user is dragging in the negative direction
      const newX = currentX < startX ? currentX : startX;
      const newY = currentY < startY ? currentY : startY;

      // Log to ensure proper values
      console.log('Drawing frame:', { newX, newY, newWidth, newHeight });

      // Create a new array reference and update the specific frame
      return prevFrames.map((frame, index) => {
        if (index === selectedCropIndex) {
          return { ...frame, x: newX, y: newY, width: newWidth, height: newHeight };
        }
        return frame;
      });
    });
  }, [isDraggingFrame, selectedCropIndex, zoomFactor]);  

  const handleMouseUp = useCallback(() => {
    if (isDraggingFrame) {
      setCropFrames((prevFrames) => {
        // Only commit if width and height are > 0
        const frame = prevFrames[selectedCropIndex];
        if (frame.width > 0 && frame.height > 0) {
          return prevFrames.map((f, idx) => {
            if (idx === selectedCropIndex) {
              const x = Math.round(f.x);
              const y = Math.round(f.y);
              const width = Math.max(1, Math.round(f.width));
              const height = Math.max(1, Math.round(f.height));
              return {
                ...f,
                x,
                y,
                width,
                height,
                originalWidth: width,
                originalHeight: height,
                isLargerThanImage: width > imageDimensions.width || height > imageDimensions.height,
              };
            }
            return f;
          });
        } else {
          // Remove the frame if it's 0x0
          return prevFrames.filter((_, idx) => idx !== selectedCropIndex);
        }
      });
      setIsDraggingFrame(false);
      const frame = cropFrames[selectedCropIndex];
      if (frame && (Math.round(frame.width) > imageDimensions.width || Math.round(frame.height) > imageDimensions.height)) {
        setPendingOversizeFrames([{ existingIndex: selectedCropIndex }]);
      }
      setRecentlyDrawnIndex(selectedCropIndex);
      if (!dialogDrawMode) setIsDrawing(false);
    }
  }, [cropFrames, dialogDrawMode, imageDimensions.height, imageDimensions.width, isDraggingFrame, selectedCropIndex]);
  
// Toggle button for drawing mode
const toggleDrawMode = () => {
  setIsDrawing((prev) => !prev);
};

// Function to clear the image
const clearImage = () => {
  setSelectedImage(null);
  setSelectedImageFile(null);
  setImageDimensions({ width: 0, height: 0 });
  setCropFrames([]);
  setCroppedImages([]);
  setSelectedImages([]);
  setZoomFactor(1); // Optionally reset zoom to 1
  // You might also want to reset any other states or references as needed
};
  const clearCrops = () => {
    setCropFrames([]);
    setCropLayerNames([]);
    setCroppedImages([]);
    setSelectedImages([]);
    setSelectedCropIndex(-1);
  };
  // Function to toggle selection of an image
  const toggleImageSelection = (index) => {
    if (selectedImages.includes(index)) {
      setSelectedImages((prevSelected) => prevSelected.filter((item) => item !== index));
    } else {
      setSelectedImages((prevSelected) => [...prevSelected, index]);
    }
  };

  // Function to check if an image is selected
  const isImageSelected = (index) => {
    return selectedImages.includes(index);
  };

  // Function to download selected images
  const downloadSelectedImages = () => {
    selectedImages.forEach((index) => {
      const croppedImageData = croppedImages[index];
      downloadCroppedImage(croppedImageData.croppedImage, croppedImageData.filename, 'png');
    });
  };

  // Function to handle dragging over window
  const handleDragOverWindow = (e) => {
    e.preventDefault(); // Prevent default behavior for dragover
    setSelectedImage(null); // Reset selectedImage when dragging over the window
    setIsDraggingOverWindow(true);
  };

  const handleDragLeaveWindow = () => {
    setIsDraggingOverWindow(false);
  };

  useEffect(() => {
    const zoomContainer = zoomContainerRef.current;
    
    if (isDrawing) {
      // Always capture mouse events at the document level to avoid interference from crop frames
      document.addEventListener('mousedown', handleMouseDown, true); // useCapture=true
      document.addEventListener('mousemove', handleMouseMove, true);
      document.addEventListener('mouseup', handleMouseUp, true);
    } else {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('mouseup', handleMouseUp, true);
    };
  }, [isDrawing, handleMouseDown, handleMouseMove, handleMouseUp]);
  
  
  useEffect(() => {
    // Add event listeners when the component mounts
    document.addEventListener('dragover', handleDragOverWindow);
    document.addEventListener('dragleave', handleDragLeaveWindow);

    // Remove event listeners when the component unmounts
    return () => {
      document.removeEventListener('dragover', handleDragOverWindow);
      document.removeEventListener('dragleave', handleDragLeaveWindow);
    };
  }, []); // Empty dependency array means this effect runs once when the component mounts

  const selectCropFrame = (index) => {
    setSelectedCropIndex(index);
  };
  
  // Memoize the centerImageInWindow function
  const centerImageInWindow = useCallback((imageWidth, imageHeight) => {
    if (!zoomContainerRef.current) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Determine the size of the zoom container
    const containerWidth = Math.max(imageWidth * zoomFactor, viewportWidth);
    const containerHeight = Math.max(imageHeight * zoomFactor, viewportHeight);

    // Update the zoom container size
    zoomContainerRef.current.style.width = `${containerWidth}px`;
    zoomContainerRef.current.style.height = `${containerHeight}px`;

    // Calculate the scroll offset to center the image
    const scrollLeft = Math.max(0, (containerWidth - viewportWidth) / 2);
    const scrollTop = Math.max(0, (containerHeight - viewportHeight) / 2);

    // Debugging output
    console.log('Container Width:', containerWidth, 'Container Height:', containerHeight);
    console.log('Scroll Left:', scrollLeft, 'Scroll Top:', scrollTop);

    // Ensure scrolling only if needed
    if (scrollLeft > 0 || scrollTop > 0) {
      window.scrollTo({
        top: scrollTop,
        left: scrollLeft,
        behavior: 'auto',
      });
    }
  }, [zoomFactor]);

  // useEffect to center image in window
  useEffect(() => {
    if (selectedImage && imageDimensions.width && imageDimensions.height) {
      // Use setTimeout to ensure layout is updated
      setTimeout(() => {
        centerImageInWindow(imageDimensions.width, imageDimensions.height);
      }, 100); // Delay to ensure the image is rendered
    }
  }, [selectedImage, imageDimensions.width, imageDimensions.height, centerImageInWindow]);

  // useEffect to handle zoom changes
  useEffect(() => {
    if (selectedImage && imageDimensions.width && imageDimensions.height) {
      centerImageInWindow(imageDimensions.width, imageDimensions.height);
    }
  }, [zoomFactor, selectedImage, imageDimensions.width, imageDimensions.height, centerImageInWindow]);


  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
        setSelectedImage(reader.result);
        if (cropFrames.length === 0) {
          setIsDrawing(true);
        }

        // Center the image in the window after it's fully loaded
        centerImageInWindow(img.width, img.height);
      };
    };

    reader.readAsDataURL(file);

    // Other state updates
    setSelectedImageFile(file);
    setIsDraggedImage(true);
    setAddCropButtonVisible(true);

    onFileDrop(acceptedFiles);
  };

  const toggleDownloads = () => {
    setShowDownloads((prev) => !prev);
  };

  const addCropFrame = (x, y, width, height) => {
    const newFrame = {
      x,
      y,
      width,
      height,
      originalWidth: width,
      originalHeight: height,
      isLargerThanImage: width > imageDimensions.width || height > imageDimensions.height,
    };
    if (newFrame.isLargerThanImage) {
      setPendingOversizeFrames([newFrame]);
      return;
    }
    const newName = `Crop Frame ${cropLayerNames.length + 1}`; // Example name
    setCropFrames((prevFrames) => {
      const updatedFrames = [...prevFrames, newFrame];
      const newIndex = updatedFrames.length - 1;
      setSelectedCropIndex(newIndex);
      return updatedFrames;
    });
    setCropLayerNames((prevNames) => [...prevNames, newName]);
  };

  const handleFrameDialogConfirm = (dimensions) => {
    if (smartFrameEnabled) {
      handleSmartCrop(dimensions);
      return;
    }
    const newFrames = dimensions.map(({ width, height, constrain }) => ({
      x: Math.max(0, Math.min(imageDimensions.width - width, imageDimensions.width * frameAnchor.x - width * frameAnchor.x)),
      y: Math.max(0, Math.min(imageDimensions.height - height, imageDimensions.height * frameAnchor.y - height * frameAnchor.y)),
      width,
      height,
      originalWidth: width,
      originalHeight: height,
      isLargerThanImage: width > imageDimensions.width || height > imageDimensions.height,
      constrain,
    }));
    const constrainedFrames = newFrames.map((frame) => frame.constrain ? constrainFrameToImage(frame) : frame);
    if (newFrames.some((frame) => frame.isLargerThanImage && !frame.constrain)) {
      setPendingOversizeFrames(constrainedFrames);
      return;
    }
    addFrames(constrainedFrames);
  };

  const addFrames = (newFrames) => {
    setCropFrames((current) => [...current, ...newFrames]);
    setCropLayerNames((current) => [
      ...current,
      ...newFrames.map((_, index) => `Crop Frame ${current.length + index + 1}`),
    ]);
    setSelectedCropIndex(cropFrames.length);
    closeSmartDialog();
  };

  const constrainFrameToImage = (frame) => {
    const outputWidth = frame.originalWidth || frame.width;
    const outputHeight = frame.originalHeight || frame.height;
    const scale = Math.min(1, imageDimensions.width / outputWidth, imageDimensions.height / outputHeight);
    const width = Math.max(1, Math.round(outputWidth * scale));
    const height = Math.max(1, Math.round(outputHeight * scale));
    return {
      ...frame,
      x: Math.max(0, Math.min(imageDimensions.width - width, frame.x)),
      y: Math.max(0, Math.min(imageDimensions.height - height, frame.y)),
      width,
      height,
    };
  };

  const handleConstrainCropFrame = (frameIndex) => {
    setCropFrames((current) => current.map((frame, index) => (
      index === frameIndex ? constrainFrameToImage(frame) : frame
    )));
  };

  const handleOversizeFramesChoice = (constrain) => {
    if (!pendingOversizeFrames) return;
    if (pendingOversizeFrames[0].existingIndex !== undefined) {
      if (constrain) {
        const frameIndex = pendingOversizeFrames[0].existingIndex;
        setCropFrames((current) => current.map((frame, index) => (
          index === frameIndex ? constrainFrameToImage(frame) : frame
        )));
      }
    } else {
      addFrames(constrain ? pendingOversizeFrames.map(constrainFrameToImage) : pendingOversizeFrames);
    }
    setPendingOversizeFrames(null);
  };

  const handleMoveCropFrame = (frameIndex, newX, newY) => {
    const updatedFrames = [...cropFrames];
    updatedFrames[frameIndex].x = newX;
    updatedFrames[frameIndex].y = newY;
    
    setCropFrames(updatedFrames);  // Ensure the state is updated correctly
    console.log('Updated frame position:', updatedFrames[frameIndex]); // Debugging log
  };
  
  const handleResizeCropFrame = (frameIndex, newWidth, newHeight, updateOriginal) => {
    const updatedFrames = [...cropFrames];
    const locked = updatedFrames[frameIndex].isLocked && updateOriginal === true;
    if (!locked) {
      updatedFrames[frameIndex].width = newWidth;
      updatedFrames[frameIndex].height = newHeight;
    }
    if (updateOriginal === true) {
      updatedFrames[frameIndex].originalWidth = newWidth;
      updatedFrames[frameIndex].originalHeight = newHeight;
    }
    const outputWidth = updatedFrames[frameIndex].originalWidth || newWidth;
    const outputHeight = updatedFrames[frameIndex].originalHeight || newHeight;
    updatedFrames[frameIndex].isLargerThanImage = outputWidth > imageDimensions.width || outputHeight > imageDimensions.height;
    setCropFrames(updatedFrames);  // Ensure the state is updated correctly
    console.log('Updated frame size:', updatedFrames[frameIndex]); // Debugging log
  };

  const handleToggleCropFrameLock = (frameIndex) => {
    setCropFrames((current) => current.map((frame, index) => (
      index === frameIndex ? { ...frame, isLocked: !frame.isLocked } : frame
    )));
  };

  const createCroppedVersions = async () => {
    if (!selectedImage || !selectedImageFile || !cropFrames.length) {
      return;
    }

    setCropLoading(true);
    try {
      const { name: originalFileNameWithExtension } = selectedImageFile;
      const originalFileName = originalFileNameWithExtension.replace(/\.[^/.]+$/, '');
      const imageElement = new Image();
      await new Promise((resolve, reject) => {
        imageElement.onload = resolve;
        imageElement.onerror = () => reject(new Error('Image could not be loaded.'));
        imageElement.src = selectedImage;
      });
      const pica = Pica(); // Initialize Pica instance

      const croppedImagesData = await Promise.all(cropFrames.map(async (frame) => {
        const outputWidth = Math.max(1, Math.round(Number(frame.originalWidth || frame.width)));
        const outputHeight = Math.max(1, Math.round(Number(frame.originalHeight || frame.height)));
        const width = Math.min(Math.max(1, Math.round(Number(frame.width))), imageElement.naturalWidth);
        const height = Math.min(Math.max(1, Math.round(Number(frame.height))), imageElement.naturalHeight);
        const x = Math.max(0, Math.min(Math.round(Number(frame.x)), imageElement.naturalWidth - width));
        const y = Math.max(0, Math.min(Math.round(Number(frame.y)), imageElement.naturalHeight - height));
  
        // Create a canvas for the cropped area
        const cropCanvas = document.createElement('canvas');
        const cropCtx = cropCanvas.getContext('2d');
        cropCanvas.width = width;
        cropCanvas.height = height;
  
        // Draw the cropped section from the image
        cropCtx.drawImage(imageElement, x, y, width, height, 0, 0, width, height);
  
        // Create a canvas for upscaling (progressive scaling)
        const upscaledCanvas = document.createElement('canvas');
        const targetWidth = outputWidth * 2;
        const targetHeight = outputHeight * 2;
        upscaledCanvas.width = targetWidth;
        upscaledCanvas.height = targetHeight;
  
        // Perform the progressive scaling using Pica
        await pica.resize(cropCanvas, upscaledCanvas, {
          unsharpAmount: 100,
          unsharpThreshold: 10,
          quality: 3, // Use highest quality for scaling
        });
  
        // Create a final canvas for the scaled-down image
        const finalCanvas = document.createElement('canvas');
        finalCanvas.width = outputWidth;
        finalCanvas.height = outputHeight;
  
        // Scale down to the final resolution using Pica
        await pica.resize(upscaledCanvas, finalCanvas);
  
        // Convert the final canvas to a data URL
        const croppedImage = finalCanvas.toDataURL('image/png', 1); // Set quality
  
        const filename = `${originalFileName}_crop_${outputWidth}x${outputHeight}`;
        return { filename, croppedImage };
      }));
  
      setCroppedImages((prevImages) => [...prevImages, ...croppedImagesData]);
      setShowDownloads(true);
    } catch (error) {
      console.error('Unable to create image crops:', error);
      alert('Unable to create image crops. Please try again.');
    } finally {
      setCropLoading(false);
    }
  };

  // Helper to send analytics events
  const sendAnalyticsEvent = (event, params = {}) => {
    if (window.gtag) {
      window.gtag('event', event, params);
    }
  };

  const downloadCroppedImage = (croppedImageDataUrl, filename, fileExtension) => {
    const a = document.createElement('a');
    a.href = croppedImageDataUrl;
    a.download = `${filename}.${fileExtension}`;
    a.click();
    sendAnalyticsEvent('download', { event_category: 'Downloads', event_label: fileExtension });
  };

  const downloadAllCroppedImages = () => {
    croppedImages.forEach((croppedImageData, index) => {
      downloadCroppedImage(croppedImageData.croppedImage, croppedImageData.filename, 'png');
    });
  };
  // Delete all cropped images
  const deleteCroppedImages = () => {
    // Clear the croppedImages state
    setCroppedImages([]);
  
    // Optionally clear the selected images as well
    setSelectedImages([]);
  };

  // Select all cropped images
  const selectAllImages = () => {
    const allIndices = croppedImages.map((_, index) => index);
    setSelectedImages(allIndices);
  };

  // Deselect all cropped images
  const deselectAllImages = () => {
    setSelectedImages([]);
  };

  // Delete only the selected cropped images
  const deleteSelectedImages = () => {
    setCroppedImages((prevCroppedImages) =>
      prevCroppedImages.filter((_, index) => !selectedImages.includes(index))
    );
    setSelectedImages([]); // Clear the selected images after deletion
  };

  const zoomIn = () => {
    setZoomFactor((prevZoom) => prevZoom * 1.1);
  };

  const zoomOut = () => {
    setZoomFactor((prevZoom) => prevZoom / 1.1);
  };

  const resetZoom = () => {
    setZoomFactor(1);
  };

  const calculateInitialZoomFactor = useCallback(() => {
    if (selectedImage) {
      const imageElement = new Image();
      imageElement.src = selectedImage;
  
      imageElement.onload = () => {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const imageWidth = imageElement.width;
        const imageHeight = imageElement.height;
  
        const widthFactor = windowWidth / imageWidth;
        const heightFactor = windowHeight / imageHeight;
  
        // Calculate initial zoom factor
        let initialZoom = Math.min(widthFactor, heightFactor);
  
        // Cap zoom factor at 1 if the image is smaller than the window
        initialZoom = Math.min(initialZoom, 1);
  
        setZoomFactor(initialZoom);
      };
    }
  }, [selectedImage]);
  
  useEffect(() => {
    calculateInitialZoomFactor();
  }, [selectedImage, calculateInitialZoomFactor]);

  const handleRemoveCropFrame = (index) => {
    setCropFrames((prevFrames) => {
      const updatedFrames = [...prevFrames];
      updatedFrames.splice(index, 1);
      return updatedFrames;
    });
    setCropLayerNames((prevNames) => {
      const updatedNames = [...prevNames];
      updatedNames.splice(index, 1);
      return updatedNames;
    });
  };

  const handleCycleFocus = (index) => {
    setCropFrames((prevFrames) => {
      const updatedFrames = [...prevFrames];
      const focusedFrame = updatedFrames[index];
      updatedFrames.splice(index, 1);
      updatedFrames.unshift(focusedFrame); // Move the focused frame to the top of the stack
      return updatedFrames;
    });
    // Keep selected frame index updated based on the reordered list
    setSelectedCropIndex(0);
  };
  
  const renderCropLayers = () => {
    return cropFrames.map((frame, index) => {
      const displayWidth = Math.round(frame.originalWidth || frame.width);
      const displayHeight = Math.round(frame.originalHeight || frame.height);
      const layerMinCh = Math.max(String(displayWidth).length, String(displayHeight).length, 1);
      return (
        <div
          key={index}
          className={`crop-layer-name${index === selectedCropIndex ? ' selected' : ''}`}
          onClick={() => selectCropFrame(index)}
        >
          {editingLayerIndex === index ? (
            <form id={`frame-edit-form-${index}`} className="frame-edit-form crop-layer-dimensions"
              onSubmit={e => {
                e.preventDefault();
                if (editLayerWidth > 0 && editLayerHeight > 0) {
                  handleResizeCropFrame(index, editLayerWidth, editLayerHeight, true);
                  setEditingLayerIndex(-1);
                }
              }}
            >
              <label className="toolbar-dialog-field">
                <span className="toolbar-dialog-field-prefix">W</span>
                <input
                  ref={layerWidthInputRef}
                  type="number"
                  value={editLayerWidth}
                  style={{ width: `${Math.max(String(editLayerWidth).length, layerMinCh)}ch` }}
                  min={1}
                  onChange={e => setEditLayerWidth(Number(e.target.value))}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && editLayerWidth > 0 && editLayerHeight > 0) {
                      e.preventDefault();
                      handleResizeCropFrame(index, editLayerWidth, editLayerHeight, true);
                      setEditingLayerIndex(-1);
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingLayerIndex(-1);
                    }
                  }}
                />
              </label>
              <label className="toolbar-dialog-field">
                <span className="toolbar-dialog-field-prefix">H</span>
                <input
                  type="number"
                  value={editLayerHeight}
                  style={{ width: `${Math.max(String(editLayerHeight).length, layerMinCh)}ch` }}
                  min={1}
                  onChange={e => setEditLayerHeight(Number(e.target.value))}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && editLayerWidth > 0 && editLayerHeight > 0) {
                      e.preventDefault();
                      handleResizeCropFrame(index, editLayerWidth, editLayerHeight, true);
                      setEditingLayerIndex(-1);
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingLayerIndex(-1);
                    }
                  }}
                />
              </label>
            </form>
          ) : (
            <div className="crop-layer-dimensions">
              <span className="toolbar-dialog-field">
                <span className="toolbar-dialog-field-prefix">W</span>
                <span className="crop-layer-dimension-value">{displayWidth}</span>
              </span>
              <span className="toolbar-dialog-field">
                <span className="toolbar-dialog-field-prefix">H</span>
                <span className="crop-layer-dimension-value">{displayHeight}</span>
              </span>
            </div>
          )}
          <div className="crop-layer-actions">
            <Tooltip message="Lock crop framing position and aspect ratio.">
              <button
                type="button"
                className={`lock-frame-btn${frame.isLocked ? ' active' : ''}`}
                aria-label="Lock crop framing"
                aria-pressed={Boolean(frame.isLocked)}
                onClick={e => { e.stopPropagation(); handleToggleCropFrameLock(index); }}
              >&#128274;</button>
            </Tooltip>
            {frame.isLargerThanImage && (
              <Tooltip message="Output exceeds image dimensions; upscaling will occur.">
                <span className="frame-alert" aria-label="Output exceeds image dimensions; upscaling will occur.">&#9888;</span>
              </Tooltip>
            )}
            {frame.isLargerThanImage && (
              <Tooltip message="Constrain crop area to image bounds.">
                <button
                  type="button"
                  className="constrain-frame-btn"
                  aria-label="Constrain to image bounds"
                  onClick={e => { e.stopPropagation(); handleConstrainCropFrame(index); }}
                >
                  <span className="constrain-icon" aria-hidden="true">
                    <span>&#8600;</span><span>&#8601;</span><span>&#8599;</span><span>&#8598;</span>
                  </span>
                </button>
              </Tooltip>
            )}
            <button
              type="button"
              className="edit-dimensions-btn frame-edit-btn"
              onClick={e => {
                e.stopPropagation();
                if (editingLayerIndex === index) {
                  if (editLayerWidth > 0 && editLayerHeight > 0) {
                    handleResizeCropFrame(index, editLayerWidth, editLayerHeight, true);
                    setEditingLayerIndex(-1);
                  }
                } else {
                  selectCropFrame(index);
                  setEditLayerWidth(displayWidth);
                  setEditLayerHeight(displayHeight);
                  setEditingLayerIndex(index);
                }
              }}
            >
              {editingLayerIndex === index ? 'Set' : 'Edit'}
            </button>
            <button type="button" className="remove-layer" onClick={e => { e.stopPropagation(); handleRemoveCropFrame(index); }} aria-label="Remove crop frame">&ndash;</button>
          </div>
        </div>
      );
    });
  };  

  const renderCropFrames = () => {
    return cropFrames.map((frame, index) => (
      <CropFrame
        key={index}
        x={frame.x}
        y={frame.y}
        width={frame.width}
        height={frame.height}
        name={cropLayerNames[index]}
        onMove={(newX, newY) => handleMoveCropFrame(index, newX, newY)}
        onResize={(newWidth, newHeight, updateOriginal) => handleResizeCropFrame(index, newWidth, newHeight, updateOriginal)}
        onSelect={() => selectCropFrame(index)}
        onRemove={() => handleRemoveCropFrame(index)}
        onConstrain={() => handleConstrainCropFrame(index)}
        isLocked={frame.isLocked}
        onToggleLock={() => handleToggleCropFrameLock(index)}
        isLargerThanImage={frame.isLargerThanImage}
        onDoubleClick={() => handleCycleFocus(index)}  // Handle double-click for cycling
        selected={index === selectedCropIndex}
        zoomFactor={zoomFactor}
        style={{ zIndex: cropFrames.length - index }}  // Set z-index based on index
        initialWidth={frame.originalWidth || frame.width}
        initialHeight={frame.originalHeight || frame.height}
        drawMode={isDrawing}
        shiftHeld={isShiftHeld}
        isDrawingFrame={isDraggingFrame && index === selectedCropIndex}
        recentlyDrawn={recentlyDrawnIndex === index}
        zIndex={index}
      />
    ));
  };  

  const renderCroppedImages = () => {
    return (
      <div>
        <section>
          <div className='filter download-tools'>
            <span>Filter</span>
            <span className="click" onClick={selectAllImages}>Select All</span>
            <span className="click" onClick={deselectAllImages}>Select None</span>
            <span className="click" onClick={deleteSelectedImages}>Delete Selected</span>
            <span className="click" onClick={deleteCroppedImages}>Delete All</span>
          </div>
          <div className='download download-tools'>
            <span>Download</span>
            <span className="click" onClick={downloadAllCroppedImages}>All</span>
            <span className="click" onClick={downloadSelectedImages}>Selected</span>
          </div>
        </section>
        <div className='grid cropped-images'>
          {croppedImages.map((croppedImageData, index) => (
            <div 
              key={index} 
              data-index={index} 
              onClick={(e) => {
                if (!e.target.classList.contains('click')) { // Only toggle if not clicking the download button
                  toggleImageSelection(index);
                }
              }}
            >
              <img 
                src={croppedImageData.croppedImage} 
                alt={`Cropped ${index + 1}`} 
              />
              <div className="break">
                <p>
                  {croppedImageData.filename.split('_').pop()}<br />
                  <span 
                    className="click" 
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent click event from bubbling up
                      downloadCroppedImage(croppedImageData.croppedImage, croppedImageData.filename, 'png');
                    }}
                  >
                    Download
                  </span>
                  <input
                    type="checkbox"
                    name="select" 
                    id="select"
                    checked={isImageSelected(index)}
                    onChange={() => toggleImageSelection(index)}
                  />
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>
    );
  };

  const zoomedContainerStyle = {
    transform: `scale(${zoomFactor})`,
    transformOrigin: 'center center',
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
  });
  
  useEffect(() => {
    document.title = 'Multicrop - App';
  }, []);

  return (
    <div className={`app${isDrawing ? ' drawing-active' : ''}${isShiftHeld ? ' shift-held' : ''}`}>
      {pendingOversizeFrames && (
        <div className="oversize-frame-dialog-backdrop" role="presentation">
          <div className="oversize-frame-dialog" role="dialog" aria-modal="true" aria-label="Constrain crop frames">
            <p>Some frames are larger than the image. Constrain them to the image bounds?</p>
            <div>
              <button type="button" onClick={() => handleOversizeFramesChoice(true)}>Yes</button>
              <button type="button" onClick={() => handleOversizeFramesChoice(false)}>No</button>
            </div>
          </div>
        </div>
      )}
      {/* Conditionally render crop-layers container if there are crop frames */}
      {cropFrames.length > 0 && (
        <Draggable handle=".toolbar-handle">
          <div className='crop-layers'>
            <div className='crop-layer-label'>Crop Frame Layers <div className="toolbar-handle inline">
                  <div className='handle'>
                    <span>::::::::::::</span>
                  </div>
                </div></div>
            {renderCropLayers()}
          </div>
        </Draggable>
      )}
      <div className='zoom-center'>
        <div className='zoom-container' ref={zoomContainerRef} style={{ width: imageDimensions.width, height: imageDimensions.height }}>
        <div className="zoomed-container" style={{
          ...zoomedContainerStyle, // Spread existing styles
          width: imageDimensions.width,
          height: imageDimensions.height }}>
            <ImageDisplay image={selectedImage} ref={imageRef} />
            {renderCropFrames()}
            {smartDialogVisible && smartFrameEnabled && imageDimensions && imageDimensions.width > 0 && (
              <FocusArea
                focusRect={focusRect && focusRect.width != null && focusRect.height != null ? focusRect : (imageDimensions ? { x: imageDimensions.width/4, y: imageDimensions.height/4, width: imageDimensions.width/2, height: imageDimensions.height/2 } : { x: 0, y: 0, width: 100, height: 100 })}
                setFocusRect={setFocusRect}
                imageWidth={imageDimensions.width || 100}
                imageHeight={imageDimensions.height || 100}
                zoomFactor={zoomFactor}
              />
            )}
          </div>
        </div>
      </div>
      <footer>&copy; {(new Date().getFullYear())} Garrett Haas</footer>
        <div className='ui'>
          <Draggable
            handle=".toolbar-handle"
            position={toolbarPosition || undefined}
            onStop={(event, data) => setToolbarPosition({ x: data.x, y: data.y })}
          >
            <div className={`toolbar${toolbarPositioned ? ' toolbar-positioned' : ''}`} ref={toolbarRef}>
              <div className="toolbar-handle inline">
                <div className='handle'>
                  <span>::::::::::::</span>
                </div>
              </div>
              <div className="zoom-buttons inline">
                <p className='inline'>Zoom: {Math.round(zoomFactor * 100)}%</p>

                <button
                  onMouseEnter={() => setIsZoomInHovered(true)}
                  onMouseLeave={() => setIsZoomInHovered(false)}
                  onClick={zoomIn}
                >
                  +
                </button>

                <button
                  onMouseEnter={() => setIsZoomOutHovered(true)}
                  onMouseLeave={() => setIsZoomOutHovered(false)}
                  onClick={zoomOut}
                >
                  –
                </button>
                <button onClick={resetZoom}>Reset Zoom</button>
              </div>
              <button onClick={openFrameDialog}>Add Crop Frames</button>
              {/* --- Crop Dropdown (Make/View Crops) --- */}
              <div
                className={`dropdown${cropDropdownOpen ? ' open' : ''}`}
                onBlur={() => setTimeout(() => setCropDropdownOpen(false), 150)}
                tabIndex={-1}
              >
                <button
                  className="dropdown-btn"
                  style={{ minWidth: undefined }}
                  onClick={() => setCropDropdownOpen(open => !open)}
                  aria-haspopup="true"
                  aria-expanded={cropDropdownOpen}
                >
                  Crop ▾
                </button>
                <div
                  className="dropdown-content"
                  style={{ display: cropDropdownOpen ? 'block' : 'none' }}
                >
                  <div className="dropdown-group">
                    <button style={{ minWidth: 120 }} onClick={() => { createCroppedVersions(); setCropDropdownOpen(false); }}>
                      Make New Image Crops
                    </button>
                    <button style={{ minWidth: 120 }} onClick={() => { toggleDownloads(); setCropDropdownOpen(false); }}>
                      View Image Crops
                    </button>
                  </div>
                </div>
              </div>
              {smartDialogVisible && (
                <>
                  <SmartCropDialog 
                    onClose={() => {
                      closeSmartDialog();
                    }}
                    onConfirm={handleFrameDialogConfirm}
                    smartFrame={smartFrameEnabled}
                    onSmartFrameChange={handleSmartFrameToggle}
                    frameAnchor={frameAnchor}
                    onFrameAnchorChange={handleFrameAnchorChange}
                    drawFrame={dialogDrawMode}
                    onDrawFrameChange={handleDialogDrawModeChange}
                    onDrawFrameCommit={closeSmartDialog}
                    onCancel={cancelFrameDialog}
                    imageDimensions={imageDimensions}
                  />
                </>
              )}
              <div
                className={`dropdown clear-button${clearDropdownOpen ? ' open' : ''}`}
                onBlur={() => setTimeout(() => setClearDropdownOpen(false), 150)}
                tabIndex={-1}
              >
                <button
                  className="dropdown-btn"
                  onClick={() => setClearDropdownOpen((open) => !open)}
                  aria-haspopup="true"
                  aria-expanded={clearDropdownOpen}
                >
                  Clear ▾
                </button>
                <div className="dropdown-content" style={{ display: clearDropdownOpen ? 'block' : 'none' }}>
                  <div className="dropdown-group">
                    <button onClick={() => { clearImage(); setClearDropdownOpen(false); }}>Image</button>
                    <button onClick={() => { clearCrops(); setClearDropdownOpen(false); }}>Crops</button>
                  </div>
                </div>
              </div>
              <div className="toolbar-handle inline">
                <div className='handle'>
                  <span>::::::::::::</span>
                </div>
              </div>
            </div>
          </Draggable>
          {!selectedImage && (
            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'drag-active' : ''}`}>
              <input {...getInputProps()} />
              <p>Drag & drop an image here, or click to select an image</p>
            </div>
          )}
        </div>
      {showDownloads && (
        <div className="downloads">
          <button className='close' onClick={toggleDownloads}><div className='icon'><span className='x'></span><span className='x'></span></div></button>
          {renderCroppedImages()}
        </div>
      )}
      {(smartLoading || cropLoading) && (
        <div className="smart-crop-loading" role="status">
          <span className="ascii-loader" aria-hidden="true">
            <span>-</span><span>\\</span><span>|</span><span>/</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default Home;